import { useEffect, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { CheckCircle, Package, Home, QrCode, Copy, Clock, Barcode } from 'lucide-react';

interface CheckoutSession {
  id: string;
  customer_name: string;
  customer_email: string;
  total_amount: number;
  appmax_order_id: string;
  completed_at: string;
}

export default function CheckoutSuccess() {
  const { token } = useParams<{ token: string }>();
  const location = useLocation();
  const [session, setSession] = useState<CheckoutSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  
  // Dados do pagamento PIX/Boleto vindos do navigate state
  const paymentData = location.state?.paymentData;
  const paymentMethod = location.state?.paymentMethod;

  useEffect(() => {
    if (token) {
      loadSession();
    }
  }, [token]);

  const loadSession = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const res = await fetch(`${apiUrl}/api/checkout/${token}`);
      const data = await res.json();

      if (data.success) {
        setSession(data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar sessão:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="mb-6">
            <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            {paymentMethod === 'pix' ? 'Pague com PIX' : paymentMethod === 'boleto' ? 'Boleto Gerado' : 'Pagamento Aprovado!'}
          </h1>

          <p className="text-lg text-gray-600 mb-8">
            {paymentMethod === 'pix' 
              ? 'Escaneie o QR Code ou copie o código PIX para finalizar o pagamento'
              : paymentMethod === 'boleto'
              ? 'Baixe o boleto ou copie a linha digitável para pagar em qualquer banco'
              : `Obrigado pela sua compra, ${session?.customer_name}!`}
          </p>

          {/* Boleto */}
          {paymentMethod === 'boleto' && paymentData?.boleto_url && (
            <div className="bg-white border-2 border-gray-200 rounded-lg p-6 mb-8">
              <div className="flex items-center justify-center mb-4">
                <Barcode className="h-6 w-6 text-blue-600 mr-2" />
                <h2 className="text-xl font-semibold text-gray-900">Boleto Bancário</h2>
              </div>
              
              <div className="space-y-4">
                <a
                  href={paymentData.boleto_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full px-6 py-3 bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-600 transition-colors text-center"
                >
                  📄 Baixar Boleto PDF
                </a>

                {paymentData.boleto_line && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Linha Digitável:
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={paymentData.boleto_line}
                        readOnly
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono bg-gray-50"
                      />
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(paymentData.boleto_line);
                          setCopied(true);
                          setTimeout(() => setCopied(false), 2000);
                        }}
                        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
                      >
                        <Copy className="h-4 w-4" />
                        {copied ? 'Copiado!' : 'Copiar'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* QR Code do PIX */}
          {paymentMethod === 'pix' && paymentData?.pix_qrcode && (
            <div className="bg-white border-2 border-gray-200 rounded-lg p-6 mb-8">
              <div className="flex items-center justify-center mb-4">
                <QrCode className="h-6 w-6 text-blue-600 mr-2" />
                <h2 className="text-xl font-semibold text-gray-900">QR Code PIX</h2>
              </div>
              
              <div className="flex justify-center mb-4">
                <img 
                  src={`data:image/png;base64,${paymentData.pix_qrcode}`} 
                  alt="QR Code PIX" 
                  className="w-64 h-64 border-2 border-gray-300 rounded-lg"
                />
              </div>

              {paymentData.pix_emv && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ou copie o código PIX:
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={paymentData.pix_emv}
                      readOnly
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono bg-gray-50"
                    />
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(paymentData.pix_emv);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      }}
                      className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
                    >
                      <Copy className="h-4 w-4" />
                      {copied ? 'Copiado!' : 'Copiar'}
                    </button>
                  </div>
                </div>
              )}

              {paymentData.pix_expiration && (
                <div className="mt-4 flex items-center justify-center text-sm text-gray-600">
                  <Clock className="h-4 w-4 mr-2" />
                  Válido até: {new Date(paymentData.pix_expiration).toLocaleString('pt-BR')}
                </div>
              )}
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
            <div className="flex items-center justify-center mb-4">
              <Package className="h-6 w-6 text-blue-600 mr-2" />
              <h2 className="text-xl font-semibold text-gray-900">Detalhes do Pedido</h2>
            </div>

            <div className="space-y-3 text-left">
              <div className="flex justify-between">
                <span className="text-gray-600">Número do Pedido:</span>
                <span className="font-semibold">#{paymentData?.order_id || session?.appmax_order_id || '-'}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-600">Valor Total:</span>
                <span className="font-semibold text-green-600">
                  R$ {paymentData?.amount ? paymentData.amount.toFixed(2) : session?.total_amount.toFixed(2)}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-600">Email:</span>
                <span className="font-semibold">{session?.customer_email}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-600">Data:</span>
                <span className="font-semibold">
                  {session?.completed_at ? new Date(session.completed_at).toLocaleString('pt-BR') : '-'}
                </span>
              </div>
            </div>
          </div>

          {paymentMethod === 'pix' ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
              <h3 className="font-semibold text-gray-900 mb-3">⚠️ Importante</h3>
              <ul className="text-left space-y-2 text-gray-600">
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  <span>O pagamento será confirmado em até 2 minutos após o pagamento</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  <span><strong>Após pagar, envie o comprovante pelo WhatsApp</strong> para agilizar a confirmação</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  <span>Você receberá uma confirmação por email e WhatsApp</span>
                </li>
              </ul>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg p-6 mb-8">
              <h3 className="font-semibold text-gray-900 mb-3">Próximos Passos</h3>
              <ul className="text-left space-y-2 text-gray-600">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Você receberá um email de confirmação em breve</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Seu pedido será processado e enviado em até X dias úteis</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Você receberá atualizações sobre o status do pedido via WhatsApp</span>
                </li>
              </ul>
            </div>
          )}

          <Link
            to="/"
            className="inline-flex items-center px-6 py-3 bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-600 transition-colors"
          >
            <Home className="h-5 w-5 mr-2" />
            Voltar para o Início
          </Link>
        </div>
      </div>
    </div>
  );
}
